import { useRef, useState, type ChangeEvent } from "react";
import { Loader2, Paperclip, Send as SendIcon, X } from "lucide-react";
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  MAX_TOTAL_ATTACHMENTS_BYTES,
  type EmailParticipant,
  type Message,
  type SendMode,
  type UploadedAttachmentRef,
} from "@onemileauto/shared-types";
import { Button } from "@onemileauto/ui";
import { RecipientInput } from "./recipient-input";
import { useSendMessage } from "../../hooks/use-send-message";
import { useUploadAttachment } from "../../hooks/use-upload-attachment";
import {
  buildInitialBody,
  buildOutboundBody,
  ensureSubjectPrefix,
  getPlainBody,
  participantLabel,
  resolveReplyRecipients,
} from "../../lib/compose-text";
import { formatFullTimestamp } from "../../lib/format";

interface ComposeSheetProps {
  mailboxId: string;
  selfEmail?: string;
  mode: SendMode;
  /**
   * Reply/forward: the specific message this composition responds to — not
   * necessarily the thread's last message. Each message in a thread has its
   * own inline Reply/Reply All/Forward (see message-card.tsx) so the user
   * picks exactly which one. Required for reply/forward, ignored for "new".
   */
  parentMessage?: Message;
  /** Reply/forward: the thread the new message is appended to. Required for reply/forward. */
  threadId?: string;
  /** Reply only: Reply All (sender + original To/Cc, minus us) vs. plain Reply (sender only). Ignored for forward/new. */
  replyAll?: boolean;
  /** Phase 9: this mailbox's signature, if it has one — prefilled into the body for a brand-new message only (see lib/compose-text.ts#buildInitialBody). Ignored for reply/forward. */
  signature?: string | null;
  onClose: () => void;
}

function bytesToLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function mbLabel(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

interface PendingAttachment {
  clientId: string;
  filename: string;
  sizeBytes: number;
  status: "uploading" | "done" | "error";
  ref?: UploadedAttachmentRef;
  error?: string;
}

/**
 * One compose UI shared across new-message/reply/forward (per the
 * architecture plan), pre-filled differently by mode. Body is plain text —
 * no rich-text editor — both to keep this simple/robust for a non-technical
 * user and because reply/forward quoting never re-embeds raw untrusted
 * inbound HTML into an outbound send (see lib/compose-text.ts). The quoted
 * original renders below a visual divider as its own read-only block, not
 * merged into the editable field, so it's always obvious what's new vs.
 * quoted.
 */
export function ComposeSheet({ mailboxId, selfEmail, mode, parentMessage, threadId, replyAll, signature, onClose }: ComposeSheetProps) {
  const replyScope = replyAll ? "all" : "sender";
  const [to, setTo] = useState<EmailParticipant[]>(() => {
    if (mode !== "reply" || !parentMessage) return [];
    return resolveReplyRecipients(replyScope, parentMessage, selfEmail).to;
  });
  const [cc, setCc] = useState<EmailParticipant[]>(() => {
    if (mode !== "reply" || !parentMessage) return [];
    return resolveReplyRecipients(replyScope, parentMessage, selfEmail).cc;
  });
  const [bcc, setBcc] = useState<EmailParticipant[]>([]);
  // Reply All prefills Cc with the rest of the original recipients — that
  // needs to be visible immediately, not hidden behind the "Add Cc/Bcc"
  // toggle, or it looks like Reply All silently dropped people.
  const [showCcBcc, setShowCcBcc] = useState(() => mode === "reply" && replyAll === true && cc.length > 0);
  const [subject, setSubject] = useState(() => {
    if (mode === "new") return "";
    const base = parentMessage?.subject || "";
    return ensureSubjectPrefix(base, mode === "reply" ? "Re: " : "Fwd: ");
  });
  const [bodyText, setBodyText] = useState(() => buildInitialBody(mode, signature));
  const [excludedAttachmentIds, setExcludedAttachmentIds] = useState<Set<string>>(new Set());
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sendMessage = useSendMessage(mailboxId);
  const uploadAttachment = useUploadAttachment(mailboxId);

  const carryAttachments = mode === "forward" && parentMessage ? parentMessage.attachments : [];
  const attachmentIds = carryAttachments.filter((a) => !excludedAttachmentIds.has(a.id)).map((a) => a.id);

  const carriedBytes = carryAttachments
    .filter((a) => !excludedAttachmentIds.has(a.id))
    .reduce((sum, a) => sum + a.sizeBytes, 0);
  const pendingBytes = pendingAttachments
    .filter((p) => p.status !== "error")
    .reduce((sum, p) => sum + p.sizeBytes, 0);
  const totalAttachedBytes = carriedBytes + pendingBytes;

  const hasUploadingAttachment = pendingAttachments.some((p) => p.status === "uploading");
  const canSend = to.length > 0 && !sendMessage.isPending && !hasUploadingAttachment;

  function toggleAttachment(id: string) {
    setExcludedAttachmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function removePendingAttachment(clientId: string) {
    setPendingAttachments((prev) => prev.filter((p) => p.clientId !== clientId));
  }

  function handleFilesSelected(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file later
    if (files.length === 0) return;

    setAttachError(null);
    let runningTotal = totalAttachedBytes;

    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        setAttachError(`"${file.name}" is too large to attach (limit ${mbLabel(MAX_ATTACHMENT_SIZE_BYTES)} per file). Try a smaller file.`);
        continue;
      }
      if (runningTotal + file.size > MAX_TOTAL_ATTACHMENTS_BYTES) {
        setAttachError(`Adding "${file.name}" would put this message's attachments over the ${mbLabel(MAX_TOTAL_ATTACHMENTS_BYTES)} total limit.`);
        continue;
      }
      runningTotal += file.size;

      const clientId = crypto.randomUUID();
      setPendingAttachments((prev) => [
        ...prev,
        { clientId, filename: file.name || "attachment", sizeBytes: file.size, status: "uploading" },
      ]);
      uploadAttachment.mutateAsync(file).then(
        (res) => {
          setPendingAttachments((prev) =>
            prev.map((p) => (p.clientId === clientId ? { ...p, status: "done", ref: res.attachment } : p)),
          );
        },
        () => {
          setPendingAttachments((prev) =>
            prev.map((p) =>
              p.clientId === clientId ? { ...p, status: "error", error: "Couldn't attach this file — try again." } : p,
            ),
          );
        },
      );
    }
  }

  async function handleSend() {
    if (!canSend) return;
    const { text, html } = buildOutboundBody(mode, bodyText, parentMessage ?? null);
    const newAttachments = pendingAttachments
      .filter((p): p is PendingAttachment & { ref: UploadedAttachmentRef } => p.status === "done" && Boolean(p.ref))
      .map((p) => p.ref);
    try {
      await sendMessage.mutateAsync({
        mode,
        threadId: mode === "new" ? undefined : threadId,
        parentMessageId: mode === "new" ? undefined : parentMessage?.id,
        to,
        cc: cc.length > 0 ? cc : undefined,
        bcc: bcc.length > 0 ? bcc : undefined,
        subject: subject.trim() || "(no subject)",
        html,
        text,
        attachmentIds: mode === "forward" ? attachmentIds : undefined,
        newAttachments: newAttachments.length > 0 ? newAttachments : undefined,
      });
      // Close immediately — instant/optimistic per the plan. The send-toast
      // stack (mounted above this sheet) takes over showing progress/Undo.
      onClose();
    } catch {
      // sendMessage.isError renders the inline message below; keep the
      // sheet open so nothing the user typed (or attached) is lost.
    }
  }

  const title = mode === "new" ? "New message" : mode === "reply" ? (replyAll ? "Reply All" : "Reply") : "Forward";

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-cream md:items-center md:justify-center md:bg-charcoal/40 md:p-6">
      <div className="flex min-h-0 w-full flex-1 flex-col bg-cream md:h-[min(85vh,44rem)] md:max-w-2xl md:flex-none md:rounded-xl md:border md:border-border md:shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-display text-lg text-charcoal">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted hover:bg-parchment-dim hover:text-charcoal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="border-b border-border px-4">
            <RecipientInput
              label="To"
              value={to}
              onChange={setTo}
              placeholder="Recipient's email address"
              autoFocus={mode !== "reply"}
              mailboxId={mailboxId}
            />
            {showCcBcc ? (
              <>
                <RecipientInput label="Cc" value={cc} onChange={setCc} placeholder="Optional" mailboxId={mailboxId} />
                <RecipientInput label="Bcc" value={bcc} onChange={setBcc} placeholder="Optional" mailboxId={mailboxId} />
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowCcBcc(true)}
                className="py-2 text-xs font-medium text-muted hover:text-rust-deep"
              >
                Add Cc/Bcc
              </button>
            )}
            <div className="flex items-center gap-3 border-t border-border py-2.5">
              <span className="w-16 shrink-0 text-sm text-muted">Subject</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="min-w-0 flex-1 bg-transparent py-1 text-[15px] text-charcoal placeholder:text-muted focus:outline-none"
              />
            </div>
          </div>

          <textarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            placeholder={mode === "new" ? "Write your message…" : "Write your reply…"}
            className="min-h-[10rem] flex-1 resize-none bg-transparent px-4 py-4 text-[15px] leading-relaxed text-charcoal placeholder:text-muted focus:outline-none"
          />

          <div className="border-t border-border px-4 py-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">Attachments</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-rust-deep hover:bg-parchment-dim"
              >
                <Paperclip size={14} />
                Attach a file
              </button>
              <input ref={fileInputRef} type="file" multiple onChange={handleFilesSelected} className="hidden" />
            </div>

            {attachError && <p className="mb-2 text-xs text-danger">{attachError}</p>}

            {(carryAttachments.length > 0 || pendingAttachments.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {carryAttachments.map((att) => {
                  const excluded = excludedAttachmentIds.has(att.id);
                  return (
                    <button
                      key={att.id}
                      type="button"
                      onClick={() => toggleAttachment(att.id)}
                      aria-pressed={!excluded}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs transition-colors ${
                        excluded
                          ? "border-border text-muted line-through opacity-60"
                          : "border-border bg-parchment-dim text-charcoal hover:border-border-strong"
                      }`}
                    >
                      <Paperclip size={14} />
                      <span className="max-w-[10rem] truncate">{att.filename}</span>
                      <span>{bytesToLabel(att.sizeBytes)}</span>
                    </button>
                  );
                })}
                {pendingAttachments.map((p) => (
                  <div
                    key={p.clientId}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
                      p.status === "error" ? "border-danger/40 text-danger" : "border-border bg-parchment-dim text-charcoal"
                    }`}
                  >
                    {p.status === "uploading" ? (
                      <Loader2 size={14} className="animate-spin text-muted" />
                    ) : (
                      <Paperclip size={14} />
                    )}
                    <span className="max-w-[10rem] truncate">{p.filename}</span>
                    <span className={p.status === "error" ? "" : "text-muted"}>
                      {p.status === "uploading" ? "Uploading…" : p.status === "error" ? p.error : bytesToLabel(p.sizeBytes)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removePendingAttachment(p.clientId)}
                      aria-label={`Remove ${p.filename}`}
                      className="flex h-5 w-5 items-center justify-center rounded-full text-muted hover:bg-border hover:text-charcoal"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quoted original — visually divided from the compose field above, per the architecture plan. Read-only; never merged into the editable text. */}
          {parentMessage && (mode === "reply" || mode === "forward") && (
            <div className="border-t-2 border-border bg-parchment-dim/40 px-4 py-4">
              <p className="mb-2 text-xs font-medium text-muted">
                {mode === "reply"
                  ? `On ${formatFullTimestamp(parentMessage.createdAt)}, ${participantLabel(parentMessage.from)} wrote:`
                  : "Forwarded message"}
              </p>
              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap border-l-2 border-border pl-3 text-sm text-charcoal/70">
                {getPlainBody(parentMessage)}
              </div>
            </div>
          )}
        </div>

        {sendMessage.isError && (
          <p className="border-t border-border px-4 py-2 text-sm text-danger">
            Something went wrong sending this. Please try again.
          </p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSend} disabled={!canSend}>
            <SendIcon size={16} />
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
